import { Formik, FormikHelpers } from "formik";
import React, { useContext, useMemo, useRef } from "react";
import styled from "styled-components";
import { createFormData } from "../../../../api/FormDataFactory";
import { useCvpQueryClient } from "../../../../api/queryClient";
import { CvpFormButton } from "../../../../generic-components/buttons/CvpFormButton";
import { CvpFileUploadBasic } from "../../../../generic-components/common/CvpFileUploadBasic";
import { CvpFormErrors } from "../../../../generic-components/common/CvpFormErrors";
import {
  UnsavedChangesBlocker,
  UnsavedChangesHandlerContext,
  useUnsavedChangesBlockerId,
} from "../../../../generic-components/common/FormikUnsavedChangesBlocker";
import { useLocStore } from "../../../../localization";
import { LocContext } from "../../../../localization/LocContext";
import { getJobRedirectProps } from "../../../../url/getJobRedirectProps";
import { useDevice } from "../../../../utils/style/useDevice";
import { Consent5000 } from "../../../common/Consent5000";
import { handleFormSubmitError } from "../../formUtils/handleFormSubmitError";
import { SPForm } from "../../formsUI/SPForm";
import { SPFormikCheckbox } from "../../formsUI/SPFormikCheckbox";
import { SPFormikTextArea } from "../../formsUI/SPFormikTextArea";

import { observer } from "mobx-react-lite";
import { authAPI } from "../../../../api/auth";
import { wt_rekruttering, wt_tilbud } from "../../../../constants/WebtempTyper";
import { CvpApplyWithConnectButton } from "../../../../generic-components/buttons/CvpApplyWithConnectButton";
import { SpFormSpinner } from "../../../../generic-components/common/SpFormSpinner";
import { redirect } from "../../../../url/redirect";
import { CVPSearchParams } from "../../../../url/wsQueryStringStore";
import { useWhoAmI } from "../../../../user/useWhoAmI";
import { ChosenAttachmentsList } from "../../formsUI/ChosenAttachmentsList";
import {
  ApplyForJobFormValues,
  useApplyForJobSchema,
} from "../useApplyForJobSchema";
import { getQuickApplyConnectRedirectUrl } from "./getQuickApplyConnectRedirectUrl";

import { useApplyWithoutProfileContext } from "./useApplyWithoutProfileContext";
import { useApplyWithoutProfileFormData } from "./useApplyWithoutProfileFormData";

// Define the max cover letter length
export const maxCoverLetterLenght = 1010;

interface ApplyWithoutProfileConsentProps {
  requireCoverLetter: boolean;
  requireCv: boolean;
}

export const ApplyWithoutProfileConsentForm = observer(
  (props: ApplyWithoutProfileConsentProps) => {
    const { requireCoverLetter, requireCv } = props;

    const { APPLY_FOR_JOB_VALIDATION } = useApplyForJobSchema(
      requireCoverLetter,
      requireCv,
      true
    );

    if (!APPLY_FOR_JOB_VALIDATION) {
      return <SpFormSpinner />;
    }

    const { formValues, setFormValues } = useApplyWithoutProfileContext();

    const L = useLocStore(LocContext.JOB);
    const { isMobile } = useDevice();
    const { details } = useWhoAmI();

    const cvpQueryClient = useCvpQueryClient();

    const unsavedChangesId = useUnsavedChangesBlockerId();
    const unsavedChangesContext = useContext(UnsavedChangesHandlerContext);

    const { isFetched, idRelatedResolved, typeRelatedResolved } =
      useApplyWithoutProfileFormData();

    // Track which button was clicked
    const submitActionRef = useRef<"connect" | "manual" | null>(null);

    const initialValues: ApplyForJobFormValues = useMemo(() => {
      return {
        applicationText: formValues.applicationText ?? "",
        consent: formValues.consent ?? false,
        attachments: formValues.attachments ?? [],
      };
    }, [formValues]);

    async function submitForm(
      values: ApplyForJobFormValues,
      helpers: FormikHelpers<ApplyForJobFormValues>
    ) {
      unsavedChangesContext.unregisterDirtyForm(unsavedChangesId); // unblock immediately (so we dont have to wait for rerender)

      if (submitActionRef.current === "connect") {
        try {
          const carryFormData = new FormData();

          if (values.attachments && values.attachments.length > 0) {
            values.attachments.forEach((file) => {
              carryFormData.append("files", file);
            });
          }
          const formData = createFormData(
            {
              concernId: details.concernId,
              relatedId: idRelatedResolved,
              relatedType: typeRelatedResolved,
              files: values.attachments,
              consent: values.consent,
              applicationText: values.applicationText,
              redirectUrl: getQuickApplyConnectRedirectUrl(),
            },
            null,
            carryFormData
          );

          const headers = { "Content-Type": null };

          const result = await authAPI.quickApplyConnect({
            formData,
            headers,
          });

          cvpQueryClient.invalidateQueries();

          // Redirect to visma connect login
          if (result.ok) {
            redirect(`${location.origin}/${result.item}`, {
              redirectRaw: true,
              mergeQs: {},
            });
          }
        } catch (err) {
          handleFormSubmitError(err, helpers);
          return;
        }
      } else if (submitActionRef.current === "manual") {
        // Save form values to context
        setFormValues({
          applicationText: values.applicationText,
          consent: values.consent,
          attachments: values.attachments,

          // Save the context state values if they exist
          firstName: formValues.firstName ?? "",
          lastName: formValues.lastName ?? "",
          email: formValues.email ?? "",
          mobile: formValues.mobile ?? "",
          address: formValues.address ?? "",
          zipCode: formValues.zipCode ?? "",
          dob: formValues.dob ?? null,
        });

        const id_rekruttering =
          typeRelatedResolved === wt_rekruttering
            ? idRelatedResolved?.toString()
            : null;
        const id_tilbud =
          typeRelatedResolved === wt_tilbud
            ? idRelatedResolved?.toString()
            : null;

        // Redirect to manual apply page
        await redirect("apply-manually", {
          mergeQs: {
            [CVPSearchParams.cvpr]: id_rekruttering,
            [CVPSearchParams.cvpt]: id_tilbud,
          },
          method: "push",
        });
      }

      submitActionRef.current = null;
    }

    // If the form is not fetched, return null
    if (!isFetched) return null;

    return (
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validateOnMount
        validationSchema={APPLY_FOR_JOB_VALIDATION}
        onSubmit={submitForm}
      >
        {(formik) => {
          return (
            <SPForm style={{ width: "100%" }}>
              <div className="form-sections-container">
                <div className="form-section">
                  {requireCoverLetter && (
                    <SPFormikTextArea
                      name="applicationText"
                      label={L.id("b67b421645ed")
                        .loc("Skriv en kort søknadstekst (maks {1} tegn)")
                        .replace("{1}", maxCoverLetterLenght.toString())}
                      required={requireCoverLetter}
                      maxLength={maxCoverLetterLenght}
                      hideErrorMessage
                      style={{
                        minHeight: "180px",
                      }}
                    />
                  )}

                  {requireCv && (
                    <>
                      <CvpFileUploadBasic
                        outlined
                        style={{ width: isMobile ? "100%" : "400px" }}
                        onFileSelected={(evt) => {
                          const attachments = evt.files as File[];
                          formik.setFieldValue("attachments", [
                            ...formik.values.attachments,
                            ...attachments,
                          ]);
                        }}
                        loading={formik.isSubmitting}
                        label={L.id("c00533793914").loc("Last opp vedlegg")}
                        multiple
                      />

                      {formik.values.attachments &&
                        formik.values.attachments.length > 0 && (
                          <ChosenAttachmentsList
                            attachments={formik.values.attachments}
                            header={L.id("90d3f125e34a").loc("Valgte vedlegg:")}
                          />
                        )}
                    </>
                  )}
                  <div>
                    <Consent5000 />

                    <SPFormikCheckbox
                      name="consent"
                      label={L.id("15ed806512b8").loc(
                        "Jeg forstår og aksepterer teksten ovenfor"
                      )}
                      showErrorMessage={false}
                      checkboxRight
                    />
                  </div>
                </div>
              </div>

              <CvpFormErrors />
              <UnsavedChangesBlocker id={unsavedChangesId} />
              <StyledButtonContainer>
                <div className="back-button-container">
                  <CvpFormButton
                    label={L.id("2caeebacac88").loc("Tilbake")}
                    severity="secondary"
                    {...getJobRedirectProps(
                      idRelatedResolved,
                      typeRelatedResolved
                    )}
                    disabled={formik.isSubmitting}
                  />
                </div>
                <div className="apply-buttons">
                  <CvpApplyWithConnectButton
                    label={L.id("f5afd7e67617").loc(
                      "Fyll ut og søk med Connect"
                    )}
                    disabled={formik.isSubmitting || !formik.values.consent}
                    onClick={() => {
                      submitActionRef.current = "connect";
                      formik.handleSubmit();
                    }}
                  />
                  <CvpFormButton
                    className="manual-button"
                    label={L.id("7a44726ee965").loc(
                      "Fyll ut og søk med skjema"
                    )}
                    icon="pi pi-arrow-right"
                    text
                    onClick={() => {
                      submitActionRef.current = "manual";
                      formik.handleSubmit();
                    }}
                    disabled={formik.isSubmitting || !formik.values.consent}
                  />
                </div>
              </StyledButtonContainer>
            </SPForm>
          );
        }}
      </Formik>
    );
  }
);

export const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  margin-top: 4rem;
  gap: 1rem;
  flex-wrap: wrap-reverse;

  .apply-buttons {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: flex-end;
    flex-grow: 1;
  }

  .back-button-container {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    margin-left: auto;
  }

  .cvp-button.manual-button {
    width: fit-content;
  }
`;
